import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type {
  ActivityId,
  NonConformityId,
  ProjectId,
  ServiceChecklistId,
  ServiceId,
  ServiceVerificationId,
  TeamMeasurementId,
  UserId,
  WeeklyAllocationId,
} from '../shared/ids';
import { QUALITY_ERROR, QUALITY_ERROR_MESSAGE } from './errors';
import { samePlaces, type NonConformityPlace } from './non-conformity-place';

/**
 * A não conformidade — o item reprovado com número (Fase 19, fatia 2).
 *
 * ```text
 * NC nº 47 · 18/07/2025 · Vigas da G3 · Execução de fôrmas
 * A forma apresentava alguns vãos por falta de forma · 12,50 m²
 * ```
 *
 * É a linha do FORM.30 da construtora: número sequencial por obra, data, local, serviço,
 * o que foi encontrado. Nasce do item `Reprovado` da checklist, na mesma transação da
 * medição (RN-220), e o número sai pelo mesmo mecanismo do número do RDO (RN-209):
 * `lastNumber + 1` dentro da transação, com o índice único do banco como segunda camada.
 *
 * ## É da medição que a abriu (Fase 37, RN-474)
 *
 * A checklist é uma por frente × serviço × dia, mas três pedreiros na mesma frente podem
 * errar em três lugares. A NC é da **medição** que a abriu — a alocação da pessoa, ou a
 * medição da equipe —, e cada medição mantém, abre ou cancela só as suas. As de antes da
 * `0084` não têm dono (RN-480).
 *
 * ## A quantidade (RN-473, RN-475)
 *
 * Quanto do serviço medido não está conforme, na unidade do serviço. É opcional, e é ela que
 * sai da produção: a medição guarda a maior quantidade das suas NCs vivas
 * (`nonConformingQuantityOf`), e o avanço, a produtividade e o painel descontam essa.
 *
 * ## A solução (RN-294, RN-476)
 *
 * Quem reprova o item pode escrever **o que foi feito para corrigir**. Com a solução, a NC
 * nasce resolvida — corrigida na hora —, com quem e quando; sem ela, nasce aberta. A
 * quantidade sai da produção nos dois casos: o retrabalho volta quando for medido de novo
 * (RN-477). Até a Fase 37 a solução era obrigatória; as NCs de antes da `0043` não têm, e a
 * tela mostra "—".
 *
 * `CANCELLED` é o outro caminho: quem responde a checklist de novo e deixa de reprovar o item
 * **na mesma medição** cancela a NC que ela abriu (RN-221) — é erro de quem mediu, e não
 * correção. O número não é reaproveitado.
 */

export type NonConformityStatus = 'OPEN' | 'RESOLVED' | 'CANCELLED';

export const NON_CONFORMITY_STATUS_LABEL: Readonly<Record<NonConformityStatus, string>> = {
  OPEN: 'Aberta',
  RESOLVED: 'Resolvida',
  CANCELLED: 'Cancelada',
};

export interface NonConformity {
  readonly id: NonConformityId;
  readonly projectId: ProjectId;
  /** Sequencial por obra, atribuído ao abrir e nunca trocado (RN-220). */
  readonly number: number;
  /** `YYYY-MM-DD`, o dia da verificação que a abriu. */
  readonly date: string;
  readonly activityId: ActivityId;
  readonly serviceId: ServiceId;
  readonly verificationId: ServiceVerificationId;
  /**
   * A ficha e o item que a abriram (`0084`). Antes da Fase 37 a ligação morava na resposta
   * da verificação; a migração a trouxe para cá. Ausentes só se ela se perdeu.
   */
  readonly checklistId?: ServiceChecklistId;
  readonly itemId?: string;
  /** Cópias, como na verificação (ADR-128): o papel não muda porque o catálogo mudou. */
  readonly checklistName: string;
  readonly itemLabel: string;
  readonly description: string;
  /** O que foi feito para corrigir (RN-294). Ausente na NC aberta (RN-476). */
  readonly solution?: string;
  /** A quantidade não conforme, na unidade do serviço (RN-473). Ausente = não medida. */
  readonly quantity?: number;
  /** A medição que a abriu (RN-474): a alocação da pessoa... */
  readonly allocationId?: WeeklyAllocationId;
  /** ...ou a medição da equipe. As duas ausentes: a NC é de antes da `0084` (RN-480). */
  readonly teamMeasurementId?: TeamMeasurementId;
  /** Os cômodos e as peças do dia onde ela foi encontrada (D9, `0089`). Ausente: nenhum. */
  readonly places?: readonly NonConformityPlace[];
  readonly status: NonConformityStatus;
  readonly openedByUserId: UserId;
  readonly openedByName: string;
  readonly openedAt: string;
  /** Quando e por quem foi resolvida (RN-476). Presentes só na `RESOLVED`. */
  readonly resolvedAt?: string;
  readonly resolvedByUserId?: UserId;
  readonly resolvedByName?: string;
  /**
   * Quem cancelou a aberta por engano, quando e por quê (RN-483, `0085`). Ausentes na cancelada
   * por quem respondeu a checklist de novo (RN-221): ali o motivo é a resposta nova.
   */
  readonly cancelledAt?: string;
  readonly cancelledByUserId?: UserId;
  readonly cancelledByName?: string;
  readonly cancelReason?: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export const MIN_NON_CONFORMITY_DESCRIPTION = 3;
export const MAX_NON_CONFORMITY_DESCRIPTION = 500;
export const MIN_NON_CONFORMITY_SOLUTION = 3;
export const MAX_NON_CONFORMITY_SOLUTION = 500;
export const MIN_NON_CONFORMITY_CANCEL_REASON = 3;
export const MAX_NON_CONFORMITY_CANCEL_REASON = 300;

/** Chave natural: o número dentro da obra. */
export function nonConformityNaturalKey(projectId: ProjectId, number: number): string {
  return `${projectId}|${String(number)}`;
}

/** A medição dona de uma NC (RN-474). */
export type NonConformityOwner =
  | { readonly kind: 'ALLOCATION'; readonly allocationId: WeeklyAllocationId }
  | { readonly kind: 'TEAM_MEASUREMENT'; readonly teamMeasurementId: TeamMeasurementId };

/** `A:<id>` ou `T:<id>` — para comparar donos sem saber de que tipo são. */
export function ownerKey(owner: NonConformityOwner): string {
  return owner.kind === 'ALLOCATION' ? `A:${owner.allocationId}` : `T:${owner.teamMeasurementId}`;
}

/** O dono da NC, como chave; `undefined` na NC de antes da `0084` (RN-480). */
export function ownerKeyOf(
  nc: Pick<NonConformity, 'allocationId' | 'teamMeasurementId'>,
): string | undefined {
  if (nc.allocationId !== undefined) return `A:${nc.allocationId}`;
  if (nc.teamMeasurementId !== undefined) return `T:${nc.teamMeasurementId}`;
  return undefined;
}

/**
 * A quantidade não conforme de uma medição (RN-475): a **maior** das NCs vivas dela.
 *
 * Não somam: dois itens reprovados no mesmo modal — a junta e o prumo — costumam ser a
 * mesma parede. A cancelada não conta; a resolvida conta, porque o retrabalho volta pela
 * remedição (RN-477). `undefined` quando nenhuma tem quantidade.
 */
export function nonConformingQuantityOf(
  ncs: readonly Pick<NonConformity, 'status' | 'quantity'>[],
): number | undefined {
  let maior: number | undefined;
  for (const nc of ncs) {
    if (nc.status === 'CANCELLED' || nc.quantity === undefined) continue;
    if (maior === undefined || nc.quantity > maior) maior = nc.quantity;
  }
  return maior;
}

function refuse(code: string, details?: Record<string, unknown>): Result<never, DomainError> {
  return err(domainError(code, QUALITY_ERROR_MESSAGE[code]!, details));
}

/**
 * A descrição do que foi encontrado (RN-220).
 *
 * Obrigatória e curta o bastante para caber numa linha da planilha: "a forma estava
 * maior que o projeto" é uma NC; um parágrafo é o campo de observação do RDO.
 */
export function parseNonConformityDescription(text: string): Result<string, DomainError> {
  const limpo = text.trim();
  if (limpo.length < MIN_NON_CONFORMITY_DESCRIPTION) {
    return refuse(QUALITY_ERROR.NON_CONFORMITY_DESCRIPTION_REQUIRED);
  }
  if (limpo.length > MAX_NON_CONFORMITY_DESCRIPTION) {
    return refuse(QUALITY_ERROR.NON_CONFORMITY_DESCRIPTION_TOO_LONG, {
      length: limpo.length,
      max: MAX_NON_CONFORMITY_DESCRIPTION,
    });
  }
  return ok(limpo);
}

/**
 * A solução aplicada (RN-294), quando escrita.
 *
 * Curta, como a descrição: "refeita a junta com argamassa nova" é uma solução; um relatório
 * é outra coisa. Desde a Fase 37 ela é opcional (RN-476): use
 * `parseOptionalNonConformitySolution` onde ela pode faltar. Esta continua recusando o
 * vazio, para quem exige a solução — a aba que encerra a NC.
 */
export function parseNonConformitySolution(text: string): Result<string, DomainError> {
  const limpo = text.trim();
  if (limpo.length < MIN_NON_CONFORMITY_SOLUTION) {
    return refuse(QUALITY_ERROR.NON_CONFORMITY_SOLUTION_REQUIRED);
  }
  if (limpo.length > MAX_NON_CONFORMITY_SOLUTION) {
    return refuse(QUALITY_ERROR.NON_CONFORMITY_SOLUTION_TOO_LONG, {
      length: limpo.length,
      max: MAX_NON_CONFORMITY_SOLUTION,
    });
  }
  return ok(limpo);
}

/**
 * A solução que pode faltar (RN-476): em branco é "ainda não corrigida", e a NC nasce aberta.
 * Escrita, vale a mesma régua da obrigatória — três letras não são "ok".
 */
export function parseOptionalNonConformitySolution(
  text: string | undefined,
): Result<string | undefined, DomainError> {
  if (text === undefined || text.trim() === '') return ok(undefined);
  return parseNonConformitySolution(text);
}

/** O status que a solução dá: com ela, resolvida; sem ela, aberta (RN-476). */
function statusDaSolucao(solution: string | undefined): 'OPEN' | 'RESOLVED' {
  return solution === undefined ? 'OPEN' : 'RESOLVED';
}

/** Quem gravou — o autor da abertura, e de quem resolve. */
export interface NonConformityActor {
  readonly by: UserId;
  readonly byName: string;
  readonly now: string;
}

export interface OpenNonConformityInput {
  readonly id: NonConformityId;
  readonly projectId: ProjectId;
  /** O próximo número da obra — quem o calcula é o caso de uso, dentro da transação. */
  readonly number: number;
  readonly date: string;
  readonly activityId: ActivityId;
  readonly serviceId: ServiceId;
  readonly verificationId: ServiceVerificationId;
  readonly checklistId: ServiceChecklistId;
  readonly itemId: string;
  readonly checklistName: string;
  readonly itemLabel: string;
  readonly description: string;
  readonly solution?: string | undefined;
  /** Já validada contra a unidade e a medição pelo caso de uso (RN-473). */
  readonly quantity?: number | undefined;
  /** A medição que a abre. Ausente só onde não há medição a quem pertencer. */
  readonly owner?: NonConformityOwner | undefined;
  /** Já resolvidos e conferidos contra o dia pelo caso de uso (RN-517). */
  readonly places?: readonly NonConformityPlace[] | undefined;
  readonly by: UserId;
  readonly byName: string;
  readonly now: string;
}

/** Os campos do dono, para espalhar na NC. */
function camposDoDono(
  owner: NonConformityOwner | undefined,
): Pick<NonConformity, 'allocationId' | 'teamMeasurementId'> {
  if (owner === undefined) return {};
  return owner.kind === 'ALLOCATION'
    ? { allocationId: owner.allocationId }
    : { teamMeasurementId: owner.teamMeasurementId };
}

/** Abre a NC (RN-220). O número é validado aqui pelo mesmo motivo do número do RDO. */
export function openNonConformity(
  input: OpenNonConformityInput,
): Result<NonConformity, DomainError> {
  if (!Number.isInteger(input.number) || input.number < 1) {
    return refuse(QUALITY_ERROR.NON_CONFORMITY_NUMBER_INVALID, { number: input.number });
  }
  const description = parseNonConformityDescription(input.description);
  if (!description.ok) return err(description.error);
  const solution = parseOptionalNonConformitySolution(input.solution);
  if (!solution.ok) return err(solution.error);
  const status = statusDaSolucao(solution.value);

  return ok({
    id: input.id,
    projectId: input.projectId,
    number: input.number,
    date: input.date,
    activityId: input.activityId,
    serviceId: input.serviceId,
    verificationId: input.verificationId,
    checklistId: input.checklistId,
    itemId: input.itemId,
    checklistName: input.checklistName,
    itemLabel: input.itemLabel,
    description: description.value,
    ...(solution.value === undefined ? {} : { solution: solution.value }),
    ...(input.quantity === undefined ? {} : { quantity: input.quantity }),
    ...camposDoDono(input.owner),
    ...(input.places === undefined || input.places.length === 0 ? {} : { places: input.places }),
    status,
    openedByUserId: input.by,
    openedByName: input.byName,
    openedAt: input.now,
    ...(status === 'RESOLVED'
      ? { resolvedAt: input.now, resolvedByUserId: input.by, resolvedByName: input.byName }
      : {}),
    version: 1,
    createdAt: input.now,
    updatedAt: input.now,
  });
}

/**
 * Responder de novo e continuar reprovando o item mantém a NC — com a descrição, a solução e a
 * quantidade de agora (RN-221). O número, o autor e a data de abertura continuam os da
 * primeira vez: a NC é a mesma, e o que mudou foi o que se escreveu sobre ela.
 *
 * A solução escrita agora resolve (com quem e quando de agora); a apagada reabre (RN-476).
 * Nada mudando, devolve o mesmo objeto — e quem chama sabe que não há o que gravar.
 */
export function updateNonConformityDetails(
  nc: NonConformity,
  details: {
    readonly description: string;
    readonly solution?: string | undefined;
    readonly quantity?: number | undefined;
    readonly places?: readonly NonConformityPlace[] | undefined;
  },
  actor: NonConformityActor,
): Result<NonConformity, DomainError> {
  const description = parseNonConformityDescription(details.description);
  if (!description.ok) return err(description.error);
  const solution = parseOptionalNonConformitySolution(details.solution);
  if (!solution.ok) return err(solution.error);

  const status = nc.status === 'CANCELLED' ? 'CANCELLED' : statusDaSolucao(solution.value);
  if (
    description.value === nc.description &&
    solution.value === nc.solution &&
    details.quantity === nc.quantity &&
    samePlaces(details.places, nc.places) &&
    status === nc.status
  ) {
    return ok(nc);
  }

  const {
    solution: _solution,
    quantity: _quantity,
    places: _places,
    resolvedAt: _resolvedAt,
    resolvedByUserId: _resolvedByUserId,
    resolvedByName: _resolvedByName,
    ...resto
  } = nc;
  const continuaResolvida = status === 'RESOLVED' && nc.status === 'RESOLVED';
  return ok({
    ...resto,
    description: description.value,
    ...(solution.value === undefined ? {} : { solution: solution.value }),
    ...(details.quantity === undefined ? {} : { quantity: details.quantity }),
    ...(details.places === undefined || details.places.length === 0
      ? {}
      : { places: details.places }),
    status,
    ...(status !== 'RESOLVED'
      ? {}
      : continuaResolvida && nc.resolvedAt !== undefined
        ? {
            resolvedAt: nc.resolvedAt,
            ...(nc.resolvedByUserId === undefined ? {} : { resolvedByUserId: nc.resolvedByUserId }),
            ...(nc.resolvedByName === undefined ? {} : { resolvedByName: nc.resolvedByName }),
          }
        : { resolvedAt: actor.now, resolvedByUserId: actor.by, resolvedByName: actor.byName }),
    version: nc.version + 1,
    updatedAt: actor.now,
  });
}

/**
 * Cancela a NC do item que deixou de ser reprovado (RN-221).
 *
 * Total de propósito: cancelar a cancelada devolve o mesmo objeto, pela mesma razão de
 * `markReportOutdated` — quem chama é o efeito colateral de responder de novo, e ele não
 * pode falhar por uma NC que já estava fora. A cancelada perde o "resolvida em": cancelar é
 * dizer que ela não existiu.
 */
export function cancelNonConformity(nc: NonConformity, now: string): NonConformity {
  if (nc.status === 'CANCELLED') return nc;
  const {
    resolvedAt: _resolvedAt,
    resolvedByUserId: _resolvedByUserId,
    resolvedByName: _resolvedByName,
    ...resto
  } = nc;
  return { ...resto, status: 'CANCELLED', version: nc.version + 1, updatedAt: now };
}

/**
 * Cancela a NC da frente tirada do dia (RN-632), aberta ou já resolvida no modal: o trabalho que
 * ela apontava não aconteceu. Como em `cancelNonConformity`, a resolvida perde o "resolvida em";
 * como na aba, guarda quem, quando e por quê. A cancelada volta como estava.
 */
export function cancelNonConformityOfRemovedFront(
  nc: NonConformity,
  reason: string,
  actor: NonConformityActor,
): Result<NonConformity, DomainError> {
  const motivo = parseNonConformityCancelReason(reason);
  if (!motivo.ok) return err(motivo.error);
  const cancelada = cancelNonConformity(nc, actor.now);
  if (cancelada === nc) return ok(nc);
  return ok({
    ...cancelada,
    cancelledAt: actor.now,
    cancelledByUserId: actor.by,
    cancelledByName: actor.byName,
    cancelReason: motivo.value,
  });
}

/** Só a aberta se encerra e se cancela pela aba (RN-482, RN-483). */
function exigeAberta(nc: NonConformity): Result<void, DomainError> {
  if (nc.status === 'OPEN') return ok(undefined);
  return refuse(QUALITY_ERROR.NON_CONFORMITY_NOT_OPEN, { status: nc.status });
}

/**
 * Encerra a aberta com a solução (RN-482) — a "Correção" e a "Data da Reinspeção e
 * Aprovação" do FORM.30: o que foi feito, quem disse e quando.
 *
 * A solução aqui é obrigatória: encerrar sem dizer o que se fez é só esconder a NC. E não mexe
 * em número nenhum (RN-477): o retrabalho volta à produção quando for medido de novo.
 */
export function resolveNonConformity(
  nc: NonConformity,
  solution: string,
  actor: NonConformityActor,
): Result<NonConformity, DomainError> {
  const aberta = exigeAberta(nc);
  if (!aberta.ok) return err(aberta.error);
  const texto = parseNonConformitySolution(solution);
  if (!texto.ok) return err(texto.error);
  return ok({
    ...nc,
    solution: texto.value,
    status: 'RESOLVED',
    resolvedAt: actor.now,
    resolvedByUserId: actor.by,
    resolvedByName: actor.byName,
    version: nc.version + 1,
    updatedAt: actor.now,
  });
}

/** O motivo de cancelar (RN-483): curto, mas dito — "aberta em duplicidade" é um motivo. */
export function parseNonConformityCancelReason(text: string): Result<string, DomainError> {
  const limpo = text.trim();
  if (limpo.length < MIN_NON_CONFORMITY_CANCEL_REASON) {
    return refuse(QUALITY_ERROR.NON_CONFORMITY_CANCEL_REASON_REQUIRED);
  }
  if (limpo.length > MAX_NON_CONFORMITY_CANCEL_REASON) {
    return refuse(QUALITY_ERROR.NON_CONFORMITY_CANCEL_REASON_TOO_LONG, {
      length: limpo.length,
      max: MAX_NON_CONFORMITY_CANCEL_REASON,
    });
  }
  return ok(limpo);
}

/**
 * Cancela a aberta por engano, com o motivo (RN-483).
 *
 * Diferente de `cancelNonConformity`, que é o efeito de responder de novo: aqui alguém olhou a
 * NC na aba e disse que ela não devia existir — quem, quando e por quê ficam nela. O número não
 * volta, e a quantidade dela volta à produção (quem recalcula a medição é o caso de uso).
 */
export function cancelOpenNonConformity(
  nc: NonConformity,
  reason: string,
  actor: NonConformityActor,
): Result<NonConformity, DomainError> {
  const aberta = exigeAberta(nc);
  if (!aberta.ok) return err(aberta.error);
  const motivo = parseNonConformityCancelReason(reason);
  if (!motivo.ok) return err(motivo.error);
  return ok({
    ...nc,
    status: 'CANCELLED',
    cancelledAt: actor.now,
    cancelledByUserId: actor.by,
    cancelledByName: actor.byName,
    cancelReason: motivo.value,
    version: nc.version + 1,
    updatedAt: actor.now,
  });
}

/** O que a tela e o papel mostram onde não há solução — a NC aberta, ou a de antes da `0043`. */
export const NO_SOLUTION = '—';

/**
 * `nº 48 — A forma estava fora do nível (solução: refeita a fôrma)` — como sai no RDO. A NC
 * sem solução sai com "—" no lugar dela (RN-296): dizer que não há é diferente de não dizer
 * nada.
 */
export function describeNonConformity(
  nc: Pick<NonConformity, 'number' | 'description' | 'solution'>,
): string {
  return `nº ${String(nc.number)} — ${nc.description} (solução: ${nc.solution ?? NO_SOLUTION})`;
}
